import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useRefreshMessages } from './useRefreshMessages';
import { useAuth } from '../contexts/AuthContext';
import type { GmailMessage } from '../types/gmail';

export function useGmailMessages(emails: string[] | string) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<GmailMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { refreshMessages, refreshing } = useRefreshMessages();

  const fetchMessages = useCallback(async () => {
    if (!user) {
      setMessages([]);
      setLoading(false);
      return;
    }

    // Handle both string and array inputs
    const emailArray = (Array.isArray(emails) ? emails : [emails])
      .filter(Boolean) // Remove empty/null values
      .map(email => email.toLowerCase());

    if (emailArray.length === 0) {
      setMessages([]);
      setLoading(false);
      return;
    }

    try {
      // First check if we have Gmail credentials
      const { data: credentials } = await supabase
        .from('nrm_gmail_credentials')
        .select('id')
        .limit(1);

      if (!credentials?.length) {
        setMessages([]);
        setLoading(false);
        return;
      }

      const { data: dbMessages, error: fetchError } = await supabase
        .from('nrm_email_messages')
        .select('*')
        .or(emailArray.map(email =>
          `from_email.ilike.%${email}%,to_emails.cs.{${email}}`
        ).join(','))
        .order('date', { ascending: false });

      if (fetchError) throw fetchError;

      setMessages((dbMessages || []).map(msg => ({
          id: msg.gmail_id,
          threadId: msg.thread_id,
          subject: msg.subject,
          snippet: msg.snippet,
          from: msg.from_email,
          to: msg.to_emails,
          date: msg.date,
          accountEmail: msg.account_email
        })));
    } catch (err) {
      // Only log error if we have Gmail credentials
      if (credentials?.length) {
        console.error('Error fetching Gmail messages:', err);
        setError('Failed to load email messages');
      }
    } finally {
      setLoading(false);
    }
  }, [emails, user]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const refetch = async () => {
    try {
      if (!user) return;

      // Refresh messages for each email address
      const emailArray = (Array.isArray(emails) ? emails : [emails])
        .filter(Boolean);

      for (const email of emailArray) {
        await refreshMessages(email);
      }
      await fetchMessages();
    } catch (err) {
      console.error('Error refreshing messages:', err);
      setError('Failed to refresh messages');
    }
  };

  return { messages, loading, error, refetch, refreshing };
}